import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import PtLogo from '@/components/PtLogo';

// ============================================================================
// MFA Enrollment route — Standalone (no Forge dependency)
// ----------------------------------------------------------------------------
// Step 1: Call saas.start_mfa_enrollment → server generates secret, returns
//         the secret + otpauth URL for QR rendering
// Step 2: User scans QR with their authenticator app, enters first 6-digit code
// Step 3: Call saas.complete_mfa_enrollment(code) → server verifies, sets
//         mfa_enabled = true if valid
// Step 4: Sign out and redirect to /login (user signs back in WITH the code)
//
// We render the QR code as an SVG via an external library-free helper. The
// secret is also displayed in human-readable form as a fallback for users
// who can't scan.
// ============================================================================

interface EnrollmentData {
    secret: string;
    otpauth_url: string;
    email: string;
    issuer: string;
}

export default function SetupMfaRoute() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { signOut } = useAuth();
    const [enrollment, setEnrollment] = useState<EnrollmentData | null>(null);
    const [qrSvg, setQrSvg] = useState<string>('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [bootstrapping, setBootstrapping] = useState(true);
    const [success, setSuccess] = useState(false);

    // Bootstrap: get a secret from the server
    useEffect(() => {
        async function start() {
            // Defensive — only authenticated users can be here
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                navigate('/login', { replace: true });
                return;
            }

            const { data, error } = await supabase.rpc('start_mfa_enrollment', {
                p_app_label: 'PatientTrac Ambulatory'
            });

            if (error) {
                setError(t('or.mfa.start_failed', { msg: error.message }));
                setBootstrapping(false);
                return;
            }

            setEnrollment(data as EnrollmentData);

            // Generate QR locally (never sends the secret to a third party)
            try {
                const svg = await makeOtpAuthQr((data as EnrollmentData).otpauth_url);
                setQrSvg(svg);
            } catch (qrErr) {
                // QR failed — user can still copy the secret manually
                console.warn('[PT-OR] QR generation failed; falling back to manual entry only', qrErr);
            }

            setBootstrapping(false);
        }
        start();
    }, [navigate]);

    async function handleVerify(e: FormEvent) {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const { data: ok, error: verifyErr } = await supabase.rpc('complete_mfa_enrollment', {
                p_code: code
            });

            if (verifyErr) {
                setError(t('or.mfa.verify_failed', { msg: verifyErr.message }));
                return;
            }

            if (!ok) {
                setError(t('or.mfa.code_mismatch'));
                return;
            }

            // Success — show confirmation, then sign out and back to login
            setSuccess(true);
            sessionStorage.removeItem('pt-or:enrollment-in-progress');

            setTimeout(async () => {
                await signOut();
                navigate('/login', { replace: true });
            }, 2500);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('or.login.err.verify_failed_short'));
        } finally {
            setLoading(false);
        }
    }

    async function handleCancel() {
        sessionStorage.removeItem('pt-or:enrollment-in-progress');
        await signOut();
        navigate('/login', { replace: true });
    }

    if (bootstrapping) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="pt-spinner mr-3" />
                <span className="text-pt-text-muted">{t('or.mfa.generating')}</span>
            </div>
        );
    }

    if (!enrollment) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="max-w-md text-center">
                    <h1 className="text-pt-red font-semibold mb-2">{t('or.mfa.unavailable')}</h1>
                    <p className="text-pt-text-muted text-sm mb-4">{error}</p>
                    <button
                        onClick={handleCancel}
                        className="px-4 py-2 border border-pt-input-border rounded-md text-pt-text hover:border-pt-gold"
                    >
                        {t('or.mfa.back_signin')}
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="max-w-md text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-pt-green/20 border border-pt-green mb-4">
                        <svg viewBox="0 0 24 24" className="w-8 h-8 text-pt-green" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-semibold mb-2">MFA enabled</h1>
                    <p className="text-pt-text-muted text-sm">
                        {t('or.mfa.success')}
                    </p>
                </div>
            </div>
        );
    }

    const hasQr = qrSvg.length > 0;

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-2xl">
                <div className="mb-6 text-center">
                    <div className="inline-flex items-center justify-center mb-4">
                        <PtLogo size={64} />
                    </div>
                    <h1 className="text-2xl font-display font-semibold tracking-wide">
                        {t('or.mfa.title')}
                    </h1>
                    <p className="text-pt-text-muted text-sm mt-1">
                        {t('or.mfa.required')}
                    </p>
                </div>

                <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
                    <ol className="space-y-6">
                        <li>
                            <div className="text-xs uppercase tracking-wide text-pt-gold font-semibold mb-2">
                                {t('or.mfa.step1')}
                            </div>
                            <p className="text-sm text-pt-text-muted">
                                {t('or.mfa.step1_body')}
                            </p>
                        </li>

                        <li>
                            <div className="text-xs uppercase tracking-wide text-pt-gold font-semibold mb-2">
                                {t('or.mfa.step2')}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-6 items-center">
                                {hasQr ? (
                                    <div
                                        className="bg-white p-4 rounded-lg flex-shrink-0"
                                        dangerouslySetInnerHTML={{ __html: qrSvg }}
                                    />
                                ) : (
                                    <div className="bg-pt-dark border border-pt-input-border p-6 rounded-lg flex-shrink-0 text-pt-text-muted text-sm text-center" style={{ width: 240, height: 240 }}>
                                        {t('or.mfa.qr_unavailable')}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-pt-text-muted mb-3">
                                        {t('or.mfa.manual')}
                                    </p>
                                    <div className="font-mono text-sm bg-pt-dark border border-pt-input-border rounded p-3 break-all">
                                        {enrollment.secret}
                                    </div>
                                    <p className="text-xs text-pt-text-dim mt-2">
                                        Account: <span className="text-pt-text">{enrollment.email}</span>
                                    </p>
                                </div>
                            </div>
                        </li>

                        <li>
                            <div className="text-xs uppercase tracking-wide text-pt-gold font-semibold mb-2">
                                {t('or.mfa.step3')}
                            </div>
                            <form onSubmit={handleVerify} className="space-y-4">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                    className="w-full px-3 py-3 bg-pt-dark border border-pt-input-border rounded-md text-pt-text text-center font-mono text-xl tracking-widest"
                                    placeholder="000000"
                                    autoFocus
                                    autoComplete="one-time-code"
                                />

                                {error && (
                                    <div className="text-pt-red text-sm py-2 px-3 bg-pt-red/10 border-l-2 border-pt-red rounded">
                                        {error}
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-5 py-2.5 border border-pt-input-border rounded-md text-pt-text hover:border-pt-gold"
                                    >
                                        {t('or.mfa.cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading || code.length !== 6}
                                        className="flex-1 py-2.5 bg-pt-gold text-pt-darker font-semibold uppercase tracking-wide rounded-md shadow-pt-glow-gold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-pt-glow-gold-strong transition-shadow"
                                    >
                                        {loading ? <span className="pt-spinner inline-block" /> : t('or.mfa.verify_enable')}
                                    </button>
                                </div>
                            </form>
                        </li>
                    </ol>
                </div>

                <p className="mt-6 text-center text-xs text-pt-text-dim">
                    {t('or.mfa.lost_device')}
                </p>
            </div>
        </div>
    );
}

// ============================================================================
// QR code generator — local rendering (no third-party calls)
// ----------------------------------------------------------------------------
// Uses the `qrcode` npm package to generate the SVG entirely in the browser.
// CRITICAL: never send the otpauth URL (which contains the TOTP secret) to a
// third-party service. The whole point of MFA is the secret never leaves the
// platform, so we render the QR locally. This is a HIPAA / security
// requirement, not a preference.
// ============================================================================

import QRCode from 'qrcode';
import { useTranslation } from '@patienttrac/i18n';

function makeOtpAuthQr(otpauthUrl: string): Promise<string> {
    return QRCode.toString(otpauthUrl, {
        type: 'svg',
        margin: 1,
        width: 240,
        color: {
            dark: '#0a1628',   // PatientTrac dark navy
            light: '#ffffff'
        },
        errorCorrectionLevel: 'M'
    });
}
